import React from "react";
import { List, Icon, Button, Label } from "semantic-ui-react";

import { Switch } from "@/components/ui/switch";
import { TableLink } from "components/BaseTableV3/TableLink";
import ACL_RELATIONSHIPS from "acl-relationships";
import withRoleCheck from "components/hocs/withRoleCheck";
import { EditContactModal as _EditContactModal } from "routes/entity/components/Modals";
import CampaignService from "services/Campaign";

// Evaluate these reactively via useAuthorized in the consuming hook rather than
// at module load: this module is eagerly imported at app boot, so a top-level
// checkIsAuthorized() would run before userAuth is in the store on a cold
// session and freeze the result as false, disabling the controls until a full
// page reload.
export const CAN_SET_CAMPAIGN_TARGET_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaignTargetContacts.create,
  ACL_RELATIONSHIPS.campaignTargetContacts.delete,
];

export const CAN_EDIT_CONTACT_PERMISSIONS = [
  ACL_RELATIONSHIPS.entityContact.edit,
];

const EditContactModal = withRoleCheck(_EditContactModal, [
  ACL_RELATIONSHIPS.entityContact.edit,
]);

const isCampaignTargetContact = (contact, campaignId) =>
  contact.campaign_targets.length > 0 &&
  contact.campaign_targets.filter(c => c.campaign_id === campaignId).length > 0;

const createToggleCampaignHandler =
  (campaign_id, entity_id, onContactUpdated) => async (contact, isIncluded) => {
    if (isIncluded) {
      await CampaignService.removeContact(campaign_id, entity_id, contact.id);
    } else {
      await CampaignService.addContact(campaign_id, entity_id, contact.id);
    }
    if (onContactUpdated) {
      await onContactUpdated();
    }
  };

const createCallNextHandler = callNextContact => async contact => {
  await callNextContact(contact);
};

export const getReferralContactsTableColumns = ({
  campaign_id,
  entity_id,
  callNextContact,
  onContactUpdated,
  canSetCampaignTarget = false,
  canEditContact = false,
}) => {
  // Create handlers once per table (not per row)
  const handleCallNext = createCallNextHandler(callNextContact);
  const handleToggleCampaign = createToggleCampaignHandler(
    campaign_id,
    entity_id,
    onContactUpdated
  );

  const columns = [
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("ID")}
        </TableLink>
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "full_name",
      size: 200,
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
      size: 150,
    },
    {
      header: "Phone Numbers",
      id: "Phone Numbers",
      accessorKey: "phone_numbers",
      enableSorting: false,
      cell: ({ row }) => (
        <List horizontal bulleted>
          {row.getValue("Phone Numbers").map(phone => (
            <List.Item key={phone.id}>
              {phone.ext ? `${phone.number}x${phone.ext}` : phone.number}
            </List.Item>
          ))}
        </List>
      ),
    },
    {
      header: "Edit Contact",
      id: "Edit Contact",
      accessorKey: "edit_contact",
      enableSorting: false,
      size: 120,
      meta: {
        isDisplay: true,
        centered: true,
      },
      cell: ({ row }) =>
        canEditContact ? (
          <EditContactModal
            key={row.getValue("ID")}
            entityId={entity_id}
            contact={row.original}
            onSuccess={onContactUpdated}
            iconTrigger={true}
          />
        ) : null,
    },
    {
      header: "Call Next",
      id: "Call Next",
      accessorKey: "call_next",
      enableSorting: false,
      size: 130,
      meta: {
        isDisplay: true,
        centered: true,
      },
      cell: ({ row }) => {
        const contact = row.original;
        const isIncluded = isCampaignTargetContact(contact, campaign_id);

        return (
          <Button
            onClick={() => handleCallNext(contact)}
            as="div"
            labelPosition="right"
            size="tiny"
            disabled={
              !contact.phone_numbers.some(
                ({ is_callable, enabled }) => is_callable && enabled
              ) || !isIncluded
            }
          >
            <Button icon>
              <Icon name="call" />
            </Button>
            <Label as="a" basic>
              Call Next
            </Label>
          </Button>
        );
      },
    },
    {
      header: "Include in Campaign",
      id: "Include in Campaign",
      accessorKey: "include_in_campaign",
      enableSorting: false,
      size: 180,
      meta: {
        isDisplay: true,
        centered: true,
      },
      cell: ({ row }) => {
        const contact = row.original;
        const isIncluded = isCampaignTargetContact(contact, campaign_id);

        return (
          <Switch
            name="isCampaignTarget"
            checked={isIncluded}
            disabled={!canSetCampaignTarget}
            onCheckedChange={() => handleToggleCampaign(contact, isIncluded)}
            aria-label="Include in campaign"
          />
        );
      },
    },
  ];

  return columns;
};
